import { useState, useEffect, useRef, useCallback } from 'react';
import { useClientSnapshot } from 'site-modules/shared/hooks/use-client-snapshot';
import { logger } from 'client/utils/logger/logger';

const NOT_ALLOWED_ERROR = 'not-allowed';
const NOT_SUPPORTED_ERROR = 'not-supported';

/**
 * Custom hook for speech recognition functionality
 * @returns {Object} Speech recognition state and controls with setTranscriptCallback
 */
export function useSpeechRecognition(isEnabled) {
  const [isListening, setIsListening] = useState(false);
  const isSupported = useClientSnapshot(() => !!(window.SpeechRecognition || window.webkitSpeechRecognition), false);
  const [isAllowed, setIsAllowed] = useState(true);
  const recognitionRef = useRef(null);
  const transcriptRef = useRef('');
  const onTranscriptChangeRef = useRef(null);

  const handleError = useCallback(error => {
    logger.warn(`Speech recognition error: ${error}`);
  }, []);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (isEnabled && SpeechRecognition && !recognitionRef.current) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true; // Continuous results as the user speaks
      recognition.interimResults = true; // Deliver results while the user is speaking
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
        transcriptRef.current = '';
      };

      recognition.onresult = event => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const transcript = event.results[i][0].transcript;

          if (event.results[i].isFinal) {
            finalTranscript += `${transcript} `;
          } else {
            interimTranscript += transcript;
          }
        }

        // Update the accumulated transcript
        if (finalTranscript) {
          transcriptRef.current += finalTranscript;
        }

        // Combine accumulated final transcript with current interim transcript
        const fullTranscript = transcriptRef.current + interimTranscript;

        onTranscriptChangeRef.current?.(fullTranscript.trim());
      };

      recognition.onerror = ({ error }) => {
        if (error === NOT_ALLOWED_ERROR) {
          setIsAllowed(false);
        }

        setIsListening(false);
        handleError(error);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    }

    return () => {
      try {
        recognitionRef.current?.abort?.();
      } catch {
        // Ignore errors during cleanup
      }
    };
  }, [handleError, isEnabled]);

  const setTranscriptCallback = useCallback(callback => {
    onTranscriptChangeRef.current = callback;
  }, []);

  const startListening = useCallback(() => {
    if (!isSupported) {
      handleError(NOT_SUPPORTED_ERROR);
      return;
    }

    if (!isListening) {
      transcriptRef.current = '';
      recognitionRef.current?.start?.();
    }
  }, [isSupported, isListening, handleError]);

  const stopListening = useCallback(() => {
    if (isListening) {
      recognitionRef.current?.stop?.();
    }
  }, [isListening]);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  return {
    isListening,
    isSupported,
    isAllowed,
    setTranscriptCallback,
    startListening,
    stopListening,
    toggleListening,
  };
}
